import { Trans, useTranslation } from 'react-i18next';

import {
    Body1,
    Body2,
    Button,
    Flex,
    Heading3,
    Heading4,
    IconSecurity,
    IconTile,
    List,
    ListItem,
    OutlinedCard,
    Stack,
    type IconName,
    getIconByName,
} from '@myphx-engineering/phx-design-system';
import type { SemanticColorVariants } from '@myphx-engineering/phx-design-system/framework';

import {
    useFillPathway,
    useFillPrescription,
} from '../../../../contexts/fill/use-fill-prescription';
import type { PrescriptionOffer } from '../../../../hooks/use-prescription-offers';
import { useProgramTerms } from '../../../../hooks/use-program-terms';
import { getAbsoluteRoute } from '../../../../util/get-absolute-route';

export type SelectInsurancePathCardProps = {
    offer: PrescriptionOffer | undefined;
};

const CardSubHeading = () => {
    const { t } = useTranslation('lilly');
    const { medication } = useFillPrescription();

    const terms = useProgramTerms(medication, 'insurance');
    const requireCommercial =
        terms?.savingsCard?.insuranceRequirement === 'ANY_COMMERCIAL';

    if (!terms) {
        return t('fill.pathSelectV2.cards.insurance.subHeadingNoCard');
    }

    if (requireCommercial) {
        return t('fill.pathSelectV2.cards.insurance.subHeadingCommercialCard');
    }

    return t('fill.pathSelectV2.cards.insurance.subHeadingWithCard');
};

const InsuranceCardBullets = () => {
    const { t } = useTranslation('lilly');

    const bullets = t('fill.pathSelectV2.cards.insurance.bullets', {
        returnObjects: true,
    }) as Array<{
        color: SemanticColorVariants;
        icon: IconName;
        key: string;
        text: string;
    }>;

    return (
        <List>
            {bullets.map((bullet) => {
                const Icon = getIconByName(bullet.icon);

                return (
                    <ListItem
                        key={bullet.key}
                        icon={
                            <IconTile
                                color={bullet.color}
                                shape="circle"
                                size="xs"
                                Icon={Icon}
                            />
                        }
                    >
                        <Body1>
                            <Trans
                                t={t}
                                ns="dynamic"
                                components={{ b: <strong /> }}
                            >
                                {bullet.text}
                            </Trans>
                        </Body1>
                    </ListItem>
                );
            })}
        </List>
    );
};

export const SelectInsurancePathCardV2 = ({
    offer,
}: SelectInsurancePathCardProps) => {
    const { t } = useTranslation('lilly');
    const { drxId } = useFillPrescription();
    const { originalPathway } = useFillPathway();

    const line1 =
        !offer || offer.headlinePriceType === 'InsuredPriceAtPharmacy'
            ? t('fill.pathSelectV2.cards.priceAtPharmacy')
            : t('fill.pathSelectV2.cards.payAsLow');
    const line2 =
        !offer || offer.headlinePriceType === 'InsuredPriceAtPharmacy'
            ? null
            : offer.headlinePriceType === 'CopayAssistance'
              ? t('fill.pathSelectV2.cards.priceWithNote', {
                    price: offer.patientCost,
                })
              : t('fill.pathSelectV2.cards.price', {
                    price: offer.patientCost,
                });
    const hasSavingsCard = offer?.headlinePriceType === 'CopayAssistance';

    return (
        <OutlinedCard
            pinFooter
            fullHeight
            data-testid="path-select-card-insurance"
            header={
                <Flex gap="sm" data-testid="path-select-card-insurance-title">
                    <IconTile shape="squircle" size="xs" Icon={IconSecurity} />
                    <Stack gap="xxxs">
                        <Heading3>
                            {t('fill.pathSelectV2.cards.insurance.heading')}
                        </Heading3>
                        <Body2>
                            <CardSubHeading />
                        </Body2>
                    </Stack>
                </Flex>
            }
            footer={
                <Button
                    fullWidth
                    link
                    mt="md"
                    to={{
                        pathname: getAbsoluteRoute('fill.drxId.root', {
                            drxId,
                        }),
                        search: `?pathway=insurance${originalPathway ? `&original-pathway=${originalPathway}` : ''}`,
                    }}
                >
                    {t('fill.pathSelectV2.cards.insurance.button')}
                </Button>
            }
        >
            <Stack gap="md">
                <Stack gap="xxxs">
                    <Body1>{line1}</Body1>
                    {line2 ? <Heading4 size="2rem">{line2}</Heading4> : null}
                    {hasSavingsCard ? (
                        <Body2>
                            <Trans
                                t={t}
                                i18nKey="fill.pathSelectV2.cards.insurance.ifCovered"
                                components={{ b: <strong /> }}
                            />
                        </Body2>
                    ) : null}
                </Stack>
                <InsuranceCardBullets />
            </Stack>
        </OutlinedCard>
    );
};
